import * as _ from "lodash";
import * as Commons from "oc-commons/Commons";
import { Vector2 } from "three";
import * as Maths2 from "oc-threejs/Maths2";
import {
  cachedEckeEpsilon,
  epsilon,
  epsilonSq,
  parallelRadEpsilon,
  Strecke2,
} from "./grundrissFunctions";

/**
 * Ein einfacher gerichteter Graph über Ecken als interne Zwischenstruktur.
 * Warum gerichtet? Für die Berechnung der Schnittpunkte und Vereinigung ist
 * ein gerichteteter besser geeignet.
 */
export interface GEcke {
  id: number;
  punkt: Vector2;
  adjazenz: Set<GEcke>;
}

type GKante = [GEcke, GEcke];

/**
 * ACHTUNG: Der erzeugte Graph hat komplett neue IDs
 * @param strecken
 */
export function streckenZuGerichterGraph(strecken: Strecke2[]): GEcke[] {
  const ecken: GEcke[] = [];
  const cachedEcke = cachedEckeEpsilon(ecken, (p) => createGEcke(Commons.createId(), p));
  for (const strecke of strecken) {
    const [a, b] = strecke.map(cachedEcke);
    a.adjazenz.add(b);
  }
  return ecken;
}

export function gerichteterGraphZuStrecken(ecken: GEcke[]): Strecke2[] {
  return ecken.flatMap((a) =>
    a.adjazenz.toArray().map((b) => [a, b].map((e) => e.punkt.clone()) as Strecke2)
  );
}

export function createGEcke(id: number, punkt: Vector2): GEcke {
  return {
    id: id,
    punkt: punkt.clone(),
    adjazenz: new Set<GEcke>(),
  };
}

/**
 * Unterteilt sich schneidende oder überlappende Strecken
 * grob O(|Kanten|^2) unter der Annahme, dass die Anzahl neuer Unions klein ist
 * @param ecken
 */
export function fügeSchnittpunkteAlsEckenHinzu(ecken: GEcke[]) {
  const cachedEcke = cachedEckeEpsilon(ecken, (p) => createGEcke(Commons.createId(), p));
  // Mappe die Ecken zu Kanten-Liste
  const kanten = ecken.flatMap((a) => a.adjazenz.toArray().map((b) => [a, b])) as GKante[];
  const { unions, findOrCreateUnion, performUnion } = createUnions<GKante | GEcke>();
  // Mappt eine GKante auf die neu gefundenen nicht kollinearen Schnittpunkte dieser GKante
  // Ermittle alle sich schneidenden oder überlappenden Kanten
  // Hier könnte man statt stupidem pairCombinations O(n²) besser auch einen komplexeren
  // Sweep-Line Intersections Algorithmus O(nlogn) verwenden,
  // allerdings dürfte die Anzahl der Kanten in realen Zaunanlagen eher klein sein.
  Commons.pairCombinations(kanten, (a: GKante, b: GKante) => {
    const [ap, bp] = [a, b].map((kante) => kante.map((ecke) => ecke.punkt));
    const intersection = Maths2.segmentIntersection(ap, bp, epsilon);
    const type = intersection?.type;
    if (type === "X" || type === "T") {
      // schneidend
      const ecke = cachedEcke(intersection.point);
      findOrCreateUnion(a).add(ecke);
      findOrCreateUnion(b).add(ecke);
    } else if (type === "I") {
      // überlappend
      performUnion(a, b);
    }
  });

  // Schneiden und Vereinigen ist im Prinzip dasselbe - eine kollineare Anordnung von Ecken wird
  // gerichtet und nach dieser Richtung aufsteigend sortiert verkettet
  for (const union of unions) {
    const ecken = sortColinearKantenOderEckenSet(union);
    Commons.pairCombinations(ecken, (a: GEcke, b: GEcke, i: number, j: number) => {
      const b_folgt_auf_a = i + 1 === j;
      if (b_folgt_auf_a) {
        a.adjazenz.add(b);
      } else {
        a.adjazenz.delete(b);
      }
      b.adjazenz.delete(a);
    });
  }
}

/**
 * Entfernt überflüssige Ecken, welche einzig kollineare Strecken verbinden
 * grob O(2*|Kanten|) unter der Annahme, dass die Anzahl neuer Unions klein ist
 * @param ecken
 */
export function entferneUnnötigeEcken(ecken: GEcke[]) {
  const toRemove = new Set<GEcke>();
  const { unions, performUnion } = createUnions<GKante>();
  const getKanten = createGetKantenUngerichtet(ecken);
  for (const a of ecken) {
    const ks = getKanten(a);
    ks.filter((k) => kantenLänge(k) < epsilon).forEach(([a, b]) => a.adjazenz.delete(b));
    if (ks.length == 0) {
      toRemove.add(a);
    } else if (ks.length === 2 && areKantenParallel(ks[0], ks[1])) {
      performUnion(ks[0], ks[1]);
    }
  }
  for (const union of unions) {
    const ecken = sortColinearKantenOderEckenSet(union);
    const start = _.first(ecken);
    const end = _.last(ecken);
    ecken.forEach((e) => {
      start.adjazenz.delete(e);
      end.adjazenz.delete(e);
    });
    start.adjazenz.add(end);
    ecken.filter((e) => e !== start && e !== end).forEach((e) => toRemove.add(e));
  }
  return ecken.filter((e) => !toRemove.has(e));
}

/**
 * Verschmilzt GEcken, die räumlich (innerhalb von epsilon) sehr nah beieinander liegen,
 * ohne neue GEcke-Objekte zu erzeugen.
 *
 * @param ecken Liste der GEcken, die bereits bereinigt wurden.
 * @returns Eine Liste der GEcken, in der nahe beieinander liegende Ecken zusammengeführt wurden.
 */
export function verschmelzeGleicheEcken(ecken: GEcke[]): GEcke[] {
  const kanonischeEcken: GEcke[] = [];
  // Mapping, das jedem Originalobjekt seine kanonische Ecke zuordnet.
  const mapping = new Map<GEcke, GEcke>();

  for (const ecke of ecken) {
    const bereitsVorhanden = kanonischeEcken.find(
      (k) => k.punkt.distanceToSquared(ecke.punkt) < epsilonSq
    );
    if (bereitsVorhanden) {
      mapping.set(ecke, bereitsVorhanden);
      bereitsVorhanden.adjazenz.addAll(ecke.adjazenz);
    } else {
      kanonischeEcken.push(ecke);
      mapping.set(ecke, ecke);
    }
  }

  // Jetzt werden die Adjazenzlisten der kanonischen Ecken neu aufgebaut.
  kanonischeEcken.forEach((kEcke) => {
    kEcke.adjazenz = kEcke.adjazenz
      .map((nachbar) => mapping.get(nachbar)!)
      .filter((nachbar) => nachbar !== kEcke);
  });

  return kanonischeEcken;
}

/**
 * Eine Datenstruktur für Abbilden von Elementen auf Vereinigungsmengen.
 * Jedes Element liegt genau in einer Vereinigungsmenge.
 * Mittels performUnion werden die Vereinigungsmengen zweier Elemente vereinigt.
 */
function createUnions<T>() {
  type Union = Set<T>;

  // statt Set könnte man eine Map verwenden, allerdings sollte die Anzahl neuer Unions
  // in realen Zaunanlagen eher klein sein, und so ist die Implementierung einfacher
  const unions = new Set<Union>();

  const findUnion = (t: T) => unions.find((u) => u.has(t));

  const findOrCreateUnion = (t: T) => {
    let u = findUnion(t);
    if (u == null) {
      u = new Set<T>().add(t);
      unions.add(u);
    }
    return u;
  };

  const performUnion = (a: T, b: T) => {
    const [au, bu] = [a, b].map(findUnion);
    if (au === bu && au != null) {
      // bereits vereinigt
    } else {
      let union: Union;
      if (au != null && bu != null) {
        au.addAll(bu);
        unions.delete(bu);
      } else {
        if (au != null || bu != null) {
          union = au ?? bu;
        } else {
          union = new Set<T>();
          unions.add(union);
        }
        union.add(a);
        union.add(b);
      }
    }
  };

  return {
    unions,
    findUnion,
    findOrCreateUnion,
    performUnion,
  };
}

function sortColinearKantenOderEckenSet(union: Set<GKante | GEcke>) {
  const ecken = new Set<GEcke>(union.toArray().flat()).toArray();
  // da alle Punkte kollinear sind, ist direction beliebig (positiv oder negativ),
  // sie muss nur eindeutig sein - lege eine fest
  const direction = Maths2.getDirection(ecken[0].punkt, ecken[1].punkt);
  // lege direction als Achse fest und sortiere nach der Projektion auf diese Achse
  ecken.sort((a, b) => direction.dot(a.punkt) - direction.dot(b.punkt));
  return ecken;
}

function areKantenParallel(a: GKante, b: GKante) {
  return Maths2.isParallel(kantenDirection(a), kantenDirection(b), parallelRadEpsilon);
}

function kantenDirection(a: GKante) {
  return Maths2.getDirection(a[0].punkt, a[1].punkt);
}

function kantenLänge(a: GKante) {
  return a[0].punkt.distanceTo(a[1].punkt);
}

/**
 * Erzeugt eine Datenstruktur, die einen ungerichteten Graphen ermöglicht, d.h. jeder
 * Ecke alle damit verbundenden (einkommenden und ausgehenden) Kanten zuordnet.
 * Erzeugung ist O(2*|Kanten|), Zugriff O(1)
 */
export function createGetKantenUngerichtet(ecken: GEcke[]) {
  const ecke2kanten = new Map<GEcke, GKante[]>();
  const getKantenUngerichtet = (e: GEcke) => {
    let a = ecke2kanten.get(e);
    if (a == null) {
      a = [];
      ecke2kanten.set(e, a);
    }
    return a;
  };
  for (const a of ecken) {
    for (const b of a.adjazenz) {
      const k = [a, b] as GKante;
      getKantenUngerichtet(a).push(k);
      getKantenUngerichtet(b).push(k);
    }
  }
  return getKantenUngerichtet;
}
